<script setup lang="ts">
import { computed } from 'vue';
import { t } from './i18n';
import {
  generateCodeSvg,
  normalizeCodeItem,
  previewSvg,
  validateCodeItem,
  type BarcodeFormat,
  type CodeDesignItem,
  type QrEcLevel,
} from './codeSvg';
import BrandColorField from '../../../src/shared/components/BrandColorField.vue';
import type { BrandColor, ColorPickerMode } from '../../../src/shared/brandPalette';

const props = defineProps<{
  item: CodeDesignItem | null;
  update: (patch: Record<string, unknown>) => void;
  colorPickerMode?: ColorPickerMode;
  brandColors?: BrandColor[];
}>();

const isCode = computed(() => props.item?.type === 'qrcode' || props.item?.type === 'barcode');
const normalized = computed(() => normalizeCodeItem(props.item ?? {}));
const validation = computed(() => validateCodeItem(props.item ?? {}));
const preview = computed(() => previewSvg(generateCodeSvg(props.item ?? {}).svg));

function patch(next: Record<string, unknown>) {
  const draft = { ...(props.item ?? {}), ...next };
  props.update({ ...next, svg: generateCodeSvg(draft).svg });
}

function setData(event: Event) {
  patch({ codeData: (event.target as HTMLTextAreaElement).value });
}

function setFormat(event: Event) {
  patch({ barcodeFormat: (event.target as HTMLSelectElement).value as BarcodeFormat });
}

function setEcLevel(event: Event) {
  patch({ ecLevel: (event.target as HTMLSelectElement).value as QrEcLevel });
}

function setColor(key: 'fgColor' | 'bgColor', value: string) {
  patch({ [key]: value });
}

function setTransparentBg() {
  patch({ bgColor: '' });
}

function setShowText(event: Event) {
  patch({ showText: (event.target as HTMLInputElement).checked });
}

function setQuietZone(event: Event) {
  patch({ quietZone: (event.target as HTMLInputElement).checked });
}
</script>

<template>
  <section v-if="isCode" class="vptw:flex vptw:flex-col vptw:gap-4 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-(--vs-surface) vptw:p-3">
    <div class="vptw:flex vptw:items-start vptw:justify-between vptw:gap-2">
      <div>
        <h3 class="vptw:m-0 vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">
          {{ normalized.type === 'qrcode' ? t('QR code') : t('Barcode') }}
        </h3>
        <p class="vptw:mt-1 vptw:mb-0 vptw:text-xs vptw:text-(--vs-ink-2)">{{ t('Vector code layer') }}</p>
      </div>
      <span
        class="vptw:rounded-full vptw:px-2 vptw:py-0.5 vptw:text-[10px] vptw:font-semibold"
        :class="validation.valid ? 'vptw:bg-(--vs-ok)/15 vptw:text-(--vs-ok)' : 'vptw:bg-(--vs-danger)/15 vptw:text-(--vs-danger)'"
      >
        {{ validation.valid ? t('Valid') : t('Check data') }}
      </span>
    </div>

    <div class="vptw:flex vptw:h-28 vptw:items-center vptw:justify-center vptw:overflow-hidden vs-code-preview vptw:rounded-lg vptw:p-2" v-html="preview" />

    <label class="vptw:block">
      <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Data') }}</span>
      <textarea
        :value="normalized.codeData"
        rows="3"
        class="vptw:w-full! vptw:resize-none vptw:rounded-xl! vptw:border vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-2! vptw:py-2! vptw:text-xs vptw:text-(--vs-ink)! vptw:shadow-none! vptw:outline-none vptw:focus:border-(--vs-accent)! vptw:focus:ring-2! vptw:focus:ring-(--vs-accent-soft)!"
        @input="setData"
      />
      <span v-if="!validation.valid" class="vptw:mt-1 vptw:block vptw:text-[11px] vptw:text-red-600">{{ validation.message }}</span>
    </label>

    <label v-if="normalized.type === 'qrcode'" class="vptw:block">
      <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Error correction') }}</span>
      <select
        :value="normalized.ecLevel"
        class="vptw:w-full! vptw:rounded-xl! vptw:border vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-2! vptw:py-2! vptw:text-xs vptw:text-(--vs-ink)! vptw:shadow-none! vptw:outline-none vptw:focus:border-(--vs-accent)! vptw:focus:ring-2! vptw:focus:ring-(--vs-accent-soft)!"
        @change="setEcLevel"
      >
        <option value="L">L</option>
        <option value="M">M</option>
        <option value="Q">Q</option>
        <option value="H">H</option>
      </select>
    </label>

    <label v-if="normalized.type === 'qrcode'" class="vptw:flex vptw:cursor-pointer vptw:items-start vptw:justify-between vptw:gap-2 vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-2">
      <span class="vptw:min-w-0">
        <span class="vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Quiet zone') }}</span>
        <span class="vptw:mt-0.5 vptw:block vptw:text-[11px] vptw:leading-4 vptw:text-(--vs-ink-3)">
          {{ normalized.bgColor && !normalized.quietZone
            ? t('Recommended with a background color: adds a border so scanners can find the code.')
            : t('Adds an empty border around the code. Turn on when printing on dark or patterned fabric.') }}
        </span>
      </span>
      <input type="checkbox" :checked="normalized.quietZone" class="vptw:mt-0.5 vptw:rounded vptw:border-(--vs-line) vptw:text-(--vs-accent)" @change="setQuietZone" />
    </label>

    <template v-else>
      <label class="vptw:block">
        <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Format') }}</span>
        <select
          :value="normalized.barcodeFormat"
          class="vptw:w-full! vptw:rounded-xl! vptw:border vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-3! vptw:py-2! vptw:text-xs vptw:text-(--vs-ink)! vptw:shadow-none! vptw:outline-none vptw:focus:border-(--vs-accent)! vptw:focus:ring-2! vptw:focus:ring-(--vs-accent-soft)!"
          @change="setFormat"
        >
          <option value="CODE128">CODE128</option>
          <option value="EAN13">EAN13</option>
          <option value="EAN8">EAN8</option>
        </select>
      </label>

      <label class="vptw:flex vptw:cursor-pointer vptw:items-center vptw:justify-between vptw:gap-2 vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-2">
        <span class="vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Show text') }}</span>
        <input type="checkbox" :checked="normalized.showText" class="vptw:rounded vptw:border-(--vs-line) vptw:text-(--vs-accent)" @change="setShowText" />
      </label>
    </template>

    <div class="vptw:grid vptw:grid-cols-2 vptw:gap-2">
      <label class="vptw:block">
        <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Foreground') }}</span>
        <BrandColorField :model-value="normalized.fgColor" :mode="colorPickerMode" :colors="brandColors" :label="t('Foreground')" @update:model-value="setColor('fgColor', $event)" />
      </label>
      <label class="vptw:block">
        <span class="vptw:mb-1.5 vptw:block vptw:text-xs vptw:font-medium vptw:text-(--vs-ink-2)">{{ t('Background') }}</span>
        <BrandColorField :model-value="normalized.bgColor || '#ffffff'" :mode="colorPickerMode" :colors="brandColors" :label="t('Background')" @update:model-value="setColor('bgColor', $event)" />
      </label>
    </div>
    <span v-if="validation.warning" class="vptw:block vptw:text-[11px] vptw:text-amber-600">{{ validation.warning }}</span>

    <button
      type="button"
      class="vptw:w-full! vptw:cursor-pointer vptw:rounded-xl! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-2! vptw:py-2! vptw:text-xs! vptw:font-medium! vptw:text-(--vs-ink-2)! vptw:hover:bg-(--vs-surface-2)!"
      @click="setTransparentBg"
    >
      {{ t('Transparent background') }}
    </button>
  </section>
</template>
